import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wallet, Gift, TrendingDown, Users, Sparkles, Disc3, CalendarDays, ArrowDownToLine, ArrowUpFromLine, Lock, LockOpen, Coins, Plane, CupSoda, Package } from "lucide-react";
import { StatCard, PageHeader, Loader, Badge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { ZaraWidget } from "@/components/ZaraWidget";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import { fmtPKR, fmtDate } from "@/lib/format";

export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cap, setCap] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
    api.get("/capital/summary").then(({ data }) => setCap(data)).catch(() => {});
  }, []);
  if (!data) return <Loader />;

  const kindColor = { stake: "text-red-400", reward: "text-emerald-400", spin: "text-orange-400", referral: "text-purple-400", bonus: "text-yellow-400", capital_credit: "text-yellow-400", deposit: "text-emerald-400", withdrawal: "text-red-400", refund: "text-blue-400", admin_adjust: "text-gray-300", aviator_bet: "text-red-400", aviator_win: "text-emerald-400", aviator_refund: "text-blue-400", cups_bet: "text-red-400", cups_win: "text-emerald-400", dnd_bet: "text-red-400", dnd_win: "text-emerald-400" };

  return (
    <div>
      <PageHeader title={`${t("welcome_back")}, ${user?.name?.split(" ")[0] || ""} 👋`} subtitle={t("tagline")} />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        <StatCard testid="stat-balance" icon={Wallet} label={t("current_balance")} value={fmtPKR(data.balance)} accent="orange" />
        <StatCard testid="stat-deposit" icon={ArrowDownToLine} label={t("total_deposit")} value={fmtPKR(data.total_deposit)} accent="green" />
        <StatCard testid="stat-withdrawal" icon={ArrowUpFromLine} label={t("total_withdrawal")} value={fmtPKR(data.total_withdrawal)} accent="red" />
        <StatCard testid="stat-rewards" icon={Gift} label={t("total_profit")} value={fmtPKR(data.total_rewards)} accent="gold" />
        <StatCard testid="stat-bonuses" icon={Sparkles} label={t("total_bonus")} value={fmtPKR(data.bonuses)} accent="purple" />
        <StatCard testid="stat-used" icon={TrendingDown} label={t("total_used")} value={fmtPKR(data.total_used)} accent="blue" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mt-4 sm:mt-6">
        <StatCard testid="stat-ref-rewards" icon={Users} label={t("referral_rewards")} value={fmtPKR(data.referral_rewards)} accent="purple" />
        <div className="ec-card p-6 flex flex-col justify-between" data-testid="dashboard-aviator-card">
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">{t("aviator")}</p>
            <p className="text-gray-400 text-sm mt-2">Cash out before the plane flies away!</p>
          </div>
          <Button onClick={() => navigate("/aviator")} className="mt-4 bg-yellow-500 hover:bg-yellow-400 text-black btn-glow font-semibold self-start" data-testid="dashboard-aviator-cta"><Plane className="w-4 h-4 me-2" />Play Aviator</Button>
          <Button onClick={() => navigate("/three-cups")} variant="outline" className="mt-2 border-orange-500/40 bg-orange-500/10 hover:bg-orange-500/20 font-semibold self-start" data-testid="dashboard-cups-cta"><CupSoda className="w-4 h-4 me-2" />Three Cups</Button>
          <Button onClick={() => navigate("/deal-or-no-deal")} variant="outline" className="mt-2 border-orange-500/40 bg-orange-500/10 hover:bg-orange-500/20 font-semibold self-start max-w-full whitespace-normal" data-testid="dashboard-dnd-cta"><Package className="w-4 h-4 me-2" />Deal or No Deal</Button>
        </div>
        <div className="ec-card p-6 flex flex-col justify-between col-span-2 lg:col-span-1">
          <div>
            <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">{t("daily_spin")}</p>
            <p className="text-gray-400 text-sm mt-2">Spin the wheel to win points!</p>
          </div>
          <Button onClick={() => navigate("/spin")} className="mt-4 bg-orange-600 hover:bg-orange-500 btn-glow font-semibold self-start" data-testid="dashboard-spin-cta"><Disc3 className="w-4 h-4 me-2" />{t("spin_now")}</Button>
        </div>
      </div>

      {cap && (
        <div className="mt-8" data-testid="capital-lock-section">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-orange-400" />
            <h3 className="font-head text-lg font-semibold text-white">Capital Lock ({t("balance")})</h3>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            <StatCard testid="stat-total-capital" icon={Coins} label="Total Capital" value={fmtPKR(cap.total_capital)} accent="gold" />
            <StatCard testid="stat-locked-capital" icon={Lock} label="Locked Capital" value={fmtPKR(cap.locked_capital)} accent="red" />
            <StatCard testid="stat-available-profit" icon={Gift} label="Available Profit" value={fmtPKR(cap.available_profit)} accent="green" />
            <StatCard testid="stat-available-balance" icon={LockOpen} label="Available Balance" value={fmtPKR(Math.max(0, cap.available_balance))} accent="orange" />
          </div>
          {cap.locked_capital > 0 && (
            <div className="ec-card p-5 mt-4 grid sm:grid-cols-3 gap-4 text-sm" data-testid="capital-lock-details">
              <div><p className="text-xs text-gray-500">Capital Lock Start</p><p className="text-white">{cap.lock_start ? fmtDate(cap.lock_start) : "-"}</p></div>
              <div><p className="text-xs text-gray-500">Capital Unlock Date</p><p className="text-white">{cap.unlock_date ? fmtDate(cap.unlock_date) : "-"}</p></div>
              <div><p className="text-xs text-gray-500">Remaining Lock Days</p><p className="text-orange-400 font-bold" data-testid="remaining-lock-days">{cap.remaining_days} days</p></div>
            </div>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 ec-card p-6">
          <h3 className="font-head text-lg font-semibold text-white mb-4">{t("recent_activity")}</h3>
          {data.recent_activity.length === 0 ? (
            <p className="text-gray-500 text-sm py-8 text-center">No activity yet. Make your first prediction!</p>
          ) : (
            <div className="divide-y divide-white/5">
              {data.recent_activity.map((tx) => (
                <div key={tx.id} className="flex items-center justify-between py-3" data-testid={`activity-${tx.id}`}>
                  <div>
                    <p className="text-sm text-white font-medium">{tx.description}</p>
                    <p className="text-xs text-gray-500">{fmtDate(tx.created_at)}</p>
                  </div>
                  <span className={`font-head font-bold ${kindColor[tx.kind] || "text-gray-300"}`}>{tx.amount >= 0 ? "+" : ""}{fmtPKR(tx.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="ec-card p-6">
          <h3 className="font-head text-lg font-semibold text-white mb-4 flex items-center gap-2"><CalendarDays className="w-5 h-5 text-orange-400" />Quick Actions</h3>
          <div className="space-y-3">
            <Button onClick={() => navigate("/events")} variant="outline" className="w-full justify-start border-white/10 bg-white/5 hover:bg-white/10" data-testid="quick-events">{t("events")}</Button>
            <Button onClick={() => navigate("/my-predictions")} variant="outline" className="w-full justify-start border-white/10 bg-white/5 hover:bg-white/10" data-testid="quick-predictions">{t("my_predictions")}</Button>
            <Button onClick={() => navigate("/kyc")} variant="outline" className="w-full justify-start border-white/10 bg-white/5 hover:bg-white/10" data-testid="quick-kyc">{t("kyc")}</Button>
            <Button onClick={() => navigate("/referral")} variant="outline" className="w-full justify-start border-white/10 bg-white/5 hover:bg-white/10" data-testid="quick-referral">{t("referral")}</Button>
          </div>
        </div>
      </div>
      <ZaraWidget />
    </div>
  );
}
